import apiClient from '../utils/apiClient';
import logger from '../utils/logger';

// This service handles all user activity data, including reload counts,
// sentences, and stories. It provides methods for fetching and updating this data.

class UserActivityService {
  // Fetch today's user activity data including reload counts, sentences, and stories
  async getUserActivity() {
    try {
      const response = await apiClient.get('/api/user-activity');
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error fetching user activity:', error);
      throw error;
    }
  }

  // Fetch just reload counts for today
  async getReloadCounts() {
    try {
      const response = await apiClient.get('/api/user-activity/reload-counts');
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('Error fetching reload counts:', error);
      throw error;
    }
  }

  // Get today's sentences for a language
  async getSentences(language) {
    try {
      const userActivity = await this.getUserActivity();
      return userActivity.sentences?.[language] || [];
    } catch (error) {
      logger.error(`Error fetching sentences for ${language}:`, error);
      throw error;
    }
  }

  // Get today's story for a language
  async getStory(language) {
    try {
      const userActivity = await this.getUserActivity();
      return userActivity.stories?.[language] || null;
    } catch (error) {
      logger.error(`Error fetching story for ${language}:`, error);
      throw error;
    }
  }

  // Get current sentence index for a language
  async getCurrentIndex(language) {
    try {
      const userActivity = await this.getUserActivity();
      return userActivity.currentIndices?.[language] || 0;
    } catch (error) {
      logger.error(`Error fetching current index for ${language}:`, error);
      throw error;
    }
  }

  // Reload sentences for a language
  async reloadSentences(language, sentences) {
    try {
      logger.debug(`Calling reloadSentences with language: ${language} and ${sentences.length} sentences`);
      logger.debug(`API URL: /api/user-activity/sentences/${language}`);
      
      // Get user plan from localStorage
      const userPlan = localStorage.getItem('userPlan') || 'trial';
      logger.debug(`Including user plan: ${userPlan} in reloadSentences request`);
      
      const data = await apiClient.post(`/api/user-activity/sentences/${language}`, {
        sentences,
        userPlan
      });
      
      logger.debug('Received response data:', data);
      return data;
    } catch (error) {
      logger.error(`Error reloading sentences for ${language}:`, error);
      throw error;
    }
  }

  // Update current sentence index
  async updateCurrentIndex(language, index) {
    try {
      const response = await apiClient.put(`/api/user-activity/sentences/${language}/index`, {
        index
      });
      const data = await response.json();
      return data;
    } catch (error) {
      logger.error(`Error updating current index for ${language}:`, error);
      throw error;
    }
  }

  // Reload story for a language
  async reloadStory(language, story) {
    try {
      logger.debug(`Calling reloadStory with language: ${language}`);
      logger.debug(`API URL: /api/user-activity/stories/${language}`);
      
      // Get user plan from localStorage
      const userPlan = localStorage.getItem('userPlan') || 'trial';
      logger.debug(`Including user plan: ${userPlan} in reloadStory request`);
      
      // Log story object structure to debug
      if (story) {
        logger.debug('Story object structure:', {
          title: story.title ? 'Present' : 'Missing',
          titleTranslation: story.titleTranslation ? 'Present' : 'Missing',
          story: Array.isArray(story.story) ? `Array with ${story.story.length} items` : 'Not an array or empty',
          translation: Array.isArray(story.translation) ? `Array with ${story.translation.length} items` : 'Not an array or empty'
        });
      } else {
        logger.error('Story object is null or undefined');
        throw new Error('Invalid story data: null or undefined');
      }
      
      // Send the request to save the story
      const data = await apiClient.post(`/api/user-activity/stories/${language}`, {
        story,
        userPlan
      });
      
      logger.debug('Received response data:', data);
      
      return data;
    } catch (error) {
      logger.error(`Error reloading story for ${language}:`, error);
      logger.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      throw error;
    }
  }

  // Check if user has reached the maximum evaluation count for today
  async canRequestEvaluation(language) {
    try {
      const userActivity = await this.getUserActivity();
      const evaluationCount = userActivity.reloadCounts?.evaluations?.[language] || 0;
      // Return true if under the limit (2), false otherwise
      return evaluationCount < 2;
    } catch (error) {
      logger.error(`Error checking evaluation count for ${language}:`, error);
      throw error;
    }
  }

  // Record that an evaluation was requested
  async recordEvaluation(language) {
    try {
      const data = await apiClient.post(`/api/user-activity/evaluations/${language}`);
      return data;
    } catch (error) {
      logger.error(`Error recording evaluation for ${language}:`, error);
      throw error;
    }
  }

  // Get message count for a specific language and mode
  async getChatMessageCount(language, mode) {
    try {
      logger.debug(`UserActivityService - Fetching chat message count for ${language} in ${mode} mode`);
      const userActivity = await this.getUserActivity();
      
      // Ensure we have a valid count by checking the structure
      if (!userActivity || !userActivity.chatMessages) {
        logger.debug(`UserActivityService - No chat messages data found, returning 0`);
        return 0;
      }
      
      const count = userActivity.chatMessages?.[mode]?.[language] || 0;
      logger.debug(`UserActivityService - Retrieved message count: ${count}`);
      return count;
    } catch (error) {
      logger.error(`Error fetching chat message count for ${language} in ${mode} mode:`, error);
      // Return 0 as a safe default
      return 0;
    }
  }
  
  // Check if user has reached maximum allowed messages (25 for trial users)
  async hasReachedMessageLimit(language, mode, userPlan) {
    try {
      // If not a trial user, they don't have a message limit
      if (userPlan !== 'trial') {
        return {
          count: 0,
          limit: Infinity,
          hasReached: false
        };
      }
      
      logger.debug(`UserActivityService - Checking message limit for ${language} in ${mode} mode, plan: ${userPlan}`);
      
      // Use the direct API endpoint instead of going through getUserActivity
      const response = await apiClient.get(
        `/api/user-activity/chat-messages/${language}/${mode}/limit?userPlan=${userPlan || 'trial'}`
      );
      
      // Process the response
      let data;
      if (response instanceof Response) {
        if (!response.ok) throw new Error(`Limit check failed: ${response.status}`);
        data = await response.json();
      } else {
        data = response;
      }
      
      logger.debug(`UserActivityService - Direct limit check result:`, data);
      
      // Validate the response data to ensure it has the expected structure
      if (!data || typeof data.count !== 'number' || typeof data.limit !== 'number') {
        logger.warn(`UserActivityService - Invalid limit check response:`, data);
        // Provide a default that doesn't block users inappropriately
        return {
          count: 0,
          limit: userPlan === 'trial' ? 25 : Infinity,
          hasReached: false
        };
      }
      
      return {
        count: data.count,
        limit: data.limit,
        hasReached: data.hasReached
      };
    } catch (error) {
      logger.error(`Error checking message limit for ${language} in ${mode} mode:`, error);
      
      // Return a safe default that doesn't block the user but shows accurate info when possible
      return {
        count: 0, // Assume no messages sent if we can't get the real count
        limit: userPlan === 'trial' ? 25 : Infinity,
        hasReached: false // Don't block the user if we can't verify
      };
    }
  }
  
  // Increment message count for a specific language and mode
  async recordChatMessage(language, mode) {
    try {
      logger.debug(`UserActivityService - Recording chat message for ${language} in ${mode} mode`);
      logger.debug(`UserActivityService - Calling API endpoint: /api/user-activity/chat-messages/${language}/${mode}`);
      
      const response = await apiClient.post(`/api/user-activity/chat-messages/${language}/${mode}`);
      logger.debug(`UserActivityService - API response:`, response);
      
      // Process and return the data consistently
      let data;
      
      // Handle different response formats
      if (response instanceof Response) {
        // If response is a Response object with json method
        if (!response.ok) throw new Error(`Recording chat message failed: ${response.status}`);
        try {
          data = await response.json();
          logger.debug(`UserActivityService - Successfully parsed response.json():`, data);
        } catch (jsonError) {
          logger.error('Error parsing JSON response:', jsonError);
          // If response.json() fails, try to use the response directly or handle as error
          // For POST, we might expect a specific structure or just success
          data = { messageCount: -1 }; // Indicate potential issue or rely on fallback
        }
      } else {
        // If apiClient already returned parsed JSON
        data = response;
      }
      
      logger.debug(`UserActivityService - Processed response data:`, data);
      
      // Ensure we have a count value to return
      if (!data || typeof data.messageCount !== 'number') {
        logger.warn('UserActivityService - Message count not found in response, fetching fresh data');
        // Fallback to fetching current count
        const activity = await this.getUserActivity();
        const count = activity.chatMessages?.[mode]?.[language] || 0;
        return { messageCount: count, mode, language };
      }
      
      return data;
    } catch (error) {
      logger.error(`Error recording chat message for ${language} in ${mode} mode:`, error);
      logger.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      // Even if there's an error, try to get the current count
      try {
        const activity = await this.getUserActivity();
        const count = activity.chatMessages?.[mode]?.[language] || 0;
        return { messageCount: count, mode, language };
      } catch (fallbackError) {
        logger.error('Failed to get fallback message count:', fallbackError);
        throw error; // Rethrow the original error if fallback fails too
      }
    }
  }
}

export default new UserActivityService(); 